/*
 * Shipment status vocabulary.
 *
 * The order endpoint returns `status` as one of the five values the admin
 * dashboard writes: "pending" (schema default), "confirmed", "processing",
 * "Dispatch" and "Delivered". The comparisons below are the same ones the
 * previous tracking page made — only the presentation around them changed,
 * so no order that used to show a step will stop showing it.
 */

/*
 * Compared case-insensitively: the dashboard has written both "Confirmed" and
 * "confirmed" over time, and an exact match left those orders showing no
 * progress at all.
 */
const norm = (status) => String(status || "").trim().toLowerCase();
const has = (list, status) => list.includes(norm(status));

const CONFIRMED = ["confirmed", "processing", "dispatch", "delivered"];
const PROCESSING = ["processing", "dispatch", "delivered"];
const DISPATCHED = ["dispatch", "delivered"];
const DELIVERED = ["delivered"];

export const TRACK_STEPS = [
  {
    key: "confirmed",
    label: "Order Confirmed",
    hint: "Payment authorised",
    reached: CONFIRMED,
  },
  {
    key: "processing",
    label: "Processing",
    hint: "Picked & packed",
    reached: PROCESSING,
  },
  {
    key: "dispatched",
    label: "Dispatched",
    hint: "Handed to carrier",
    reached: DISPATCHED,
  },
  {
    key: "delivered",
    label: "Delivered",
    hint: "Arrived with you",
    reached: DELIVERED,
  },
];

/* Pending orders sit before step one, so nothing is marked reached yet. */
export const getStepStates = (status) => {
  const reachedFlags = TRACK_STEPS.map((step) => has(step.reached, status));
  const currentIndex = reachedFlags.lastIndexOf(true);

  return TRACK_STEPS.map((step, index) => ({
    ...step,
    done: reachedFlags[index] && index < currentIndex,
    current: index === currentIndex,
    pending: !reachedFlags[index],
  }));
};

/* Percentage of the connector line to paint, for the progress bar fill. */
export const getProgressPercent = (status) => {
  const states = getStepStates(status);
  const currentIndex = states.findIndex((step) => step.current);
  if (currentIndex < 0) return 0;
  return (currentIndex / (TRACK_STEPS.length - 1)) * 100;
};

/* "Dispatch" is a stage name in the data but reads oddly as a label. */
const DISPLAY_LABELS = {
  pending: "Pending",
  confirmed: "Confirmed",
  processing: "Processing",
  dispatch: "Dispatched",
  delivered: "Delivered",
};

export const getStatusLabel = (status) =>
  DISPLAY_LABELS[norm(status)] ||
  String(status || "pending").replace(/^\w/, (c) => c.toUpperCase());

/* Drives the badge colour: neutral while waiting, green once complete. */
export const getStatusTone = (status) => {
  if (has(DELIVERED, status)) return "done";
  if (has(DISPATCHED, status)) return "transit";
  if (has(CONFIRMED, status)) return "active";
  return "wait";
};
